import { Controller } from '@hotwired/stimulus'
import debouncedResize from '../utils/debounced_resize'
import scrollTick from '../utils/scroll_tick'
import visibilityLoop from '../utils/visibility_loop'

// Binds a pinned stage to scroll position. The controller element is a tall
// wrapper; the stage inside it sticks under the navbar, and how far the reader
// has scrolled through the wrapper becomes `--p` (0 to 1) on the stage. CSS
// draws the rest off that one number, so scrolling back rewinds it.
//
//   <div data-controller="scroll-scrub" data-scroll-scrub-days-value="80"
//        data-scroll-scrub-chapters-value="[0.15, 0.62]">
//     <div data-scroll-scrub-target="stage">
//       <span data-scroll-scrub-target="day"></span>
//       <button data-scroll-scrub-target="chapter" data-scroll-scrub-at-param="0.05"
//               data-action="scroll-scrub#jump">...</button>
//     </div>
//   </div>
//
// `days` scales `--p` into the day counter. `chapters` are the progress values
// at which the line moves on to its next chapter: the stage carries
// `data-chapter="N"` (CSS swaps the caption on it) and the Nth chapter target
// is `is-active`. A chapter button's `at` param is the progress its click
// scrolls to.
//
// When the chapters are blocks of unequal height (the cards on a timeline),
// mark each block a `marker` target instead of guessing `chapters`: the
// chapter changes as each marker crosses the middle of the viewport, and a
// chapter button with no `at` param jumps to its marker.
//
// `mode="timer"` plays the same stage on a clock instead of the scrollbar:
// `--p` runs 0 to 1 over `duration` ms while the stage is on screen, holds,
// and replays. Nothing pins; a chapter button jumps the clock.
export default class extends Controller {
  static targets = ['stage', 'day', 'chapter', 'marker']
  static values = {
    days: { type: Number, default: 100 },
    chapters: { type: Array, default: [] },
    mode: { type: String, default: 'scroll' },
    duration: { type: Number, default: 14000 },
    hold: { type: Number, default: 2500 }
  }

  connect() {
    // Cleared here, not just left undefined: a Turbo-cached page reconnects the
    // same instance, and a chapter held over from the last visit would skip the
    // first write and leave the rail painted for the wrong step.
    this.lastChapter = null
    // Under reduced motion the stage stays unpinned at the markup's `--p: 1`.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (this.modeValue === 'timer') {
      // Nothing pins, so there is no layout to measure; the chapter list is
      // the `chapters` value, read once rather than on every frame.
      this.chaptersCache = this.chaptersValue
      this.apply(0)
      this.goLive()
      this.stopTicking = visibilityLoop(this.element, {
        start: () => this.startClock(0),
        stop: () => this.stopClock()
      })
      return
    }
    this.measure()
    // The real progress is written BEFORE the stage goes live. The markup
    // carries the end-of-timeline values so the page reads without JavaScript,
    // and going live first painted a full rail for a frame — the line appeared
    // to shoot down through every dot and come back on load.
    this.tick()
    this.goLive()
    this.resizeObserver = new ResizeObserver(() => this.measure())
    this.resizeObserver.observe(this.element)
    // A viewport-height-only change (devtools pane, browser chrome) leaves the
    // wrapper's box alone, so the observer stays quiet; the sticky offset and
    // the marker thresholds still move with the viewport.
    this.stopResize = debouncedResize(() => this.measure())
    this.stopTicking = scrollTick(this.element, { tick: () => this.tick() })
  }

  // Lets the stylesheet show progress-driven parts now that the first real
  // value is on the element.
  goLive() {
    this.stageTarget.classList.add('is-live')
  }

  disconnect() {
    if (this.stopTicking) this.stopTicking()
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.stopResize) this.stopResize()
    this.stopClock()
  }

  // The wrapper's travel, the sticky offset and where each marker sits only
  // change when the wrapper's size does (a resize, an image or font landing),
  // so they are read then rather than on every scroll frame.
  measure() {
    this.travelCache = this.travel()
    this.stickyTopCache = this.stickyTop()
    this.chaptersCache = this.chapters()
  }

  startClock(progress) {
    this.stopClock()
    const startedAt = performance.now() - progress * this.durationValue
    const step = (now) => {
      const elapsed = now - startedAt
      const done = elapsed >= this.durationValue
      this.apply(done ? 1 : elapsed / this.durationValue)
      if (done) {
        this.holdTimer = setTimeout(() => this.startClock(0), this.holdValue)
      } else {
        this.frame = requestAnimationFrame(step)
      }
    }
    this.frame = requestAnimationFrame(step)
  }

  // The clock is a rAF loop while it runs and a timeout while it holds; both
  // have to go when the stage leaves the screen or the controller disconnects.
  stopClock() {
    cancelAnimationFrame(this.frame)
    clearTimeout(this.holdTimer)
  }

  jump(event) {
    event.preventDefault()
    if (this.modeValue === 'timer') {
      this.startClock(Number(event.params.at) || 0)
      return
    }
    const marker = this.markerTargets[this.chapterTargets.indexOf(event.currentTarget)]
    if (marker && event.params.at === undefined) {
      marker.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const at = Number(event.params.at) || 0
    const top = this.element.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: top - this.stickyTop() + at * this.travel(), behavior: 'smooth' })
  }

  // Progress runs while the stage is stuck: from the wrapper's top reaching the
  // sticky offset until the wrapper's bottom lets the stage go again.
  tick() {
    const top = this.element.getBoundingClientRect().top
    const travel = this.travelCache
    const progress = travel > 0 ? Math.min(Math.max((this.stickyTopCache - top) / travel, 0), 1) : 1
    this.apply(progress)
  }

  apply(progress) {
    this.stageTarget.style.setProperty('--p', progress.toFixed(4))
    if (this.hasDayTarget) this.dayTarget.textContent = Math.round(progress * this.daysValue)

    const chapters = this.chaptersCache
    const chapter = chapters.filter((at) => progress >= at).length
    this.stageTarget.style.setProperty('--chapter-p', this.chapterProgress(chapters, chapter, progress).toFixed(4))

    // `--p` and `--chapter-p` drive the continuous motion and have to be written
    // every frame; the chapter only turns over a handful of times in a run, so
    // the class and attribute writes are skipped while it holds. Timer mode
    // calls this 60×/s for the whole scene, which is what makes it worth it.
    if (chapter === this.lastChapter) return

    this.lastChapter = chapter
    this.stageTarget.dataset.chapter = chapter
    this.chapterTargets.forEach((el, index) => {
      el.classList.toggle('is-active', index === chapter)
      el.classList.toggle('is-passed', index < chapter)
      el.setAttribute('aria-current', index === chapter ? 'step' : 'false')
    })
  }

  // The chapter as a fraction of the way through the list — 2.4 is 40% of the
  // way from the third chapter's threshold to the fourth's — so a rail can fill
  // chapter by chapter and reach a dot exactly when its chapter begins.
  chapterProgress(chapters, chapter, progress) {
    // Past the last chapter there is no next dot to fill towards: the rail
    // stops on the last mark rather than running on to the wrapper's end.
    if (chapter >= chapters.length) return chapter
    const start = chapter === 0 ? 0 : chapters[chapter - 1]
    const end = chapters[chapter]
    const fraction = end > start ? (progress - start) / (end - start) : 0
    return chapter + Math.min(Math.max(fraction, 0), 1)
  }

  // Progress at which each marker after the first reaches the middle of the
  // viewport: a marker `d` below the wrapper's top gets there once the wrapper
  // has moved `d - stickyTop - viewport/2` past its pinned position.
  chapters() {
    if (!this.hasMarkerTarget) return this.chaptersValue
    const travel = this.travel()
    if (travel <= 0) return []
    const wrapperTop = this.element.getBoundingClientRect().top
    const middle = this.stickyTop() + window.innerHeight * 0.45
    return this.markerTargets.slice(1).map((marker) => {
      const distance = marker.getBoundingClientRect().top - wrapperTop
      return (distance - middle) / travel
    })
  }

  travel() {
    return this.element.offsetHeight - this.stageTarget.offsetHeight
  }

  stickyTop() {
    return parseFloat(getComputedStyle(this.stageTarget).top) || 0
  }
}
